import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './ProcessPicker.scss';

export interface ProcessPickerOption {
    id: number;
    label: string;
}

export interface ProcessPickerProps {
    /** Full list of options. */
    options: ProcessPickerOption[];
    /** Currently selected IDs (plain numbers, NOT stringified — fixes the old bug). */
    selectedIds: number[];
    /** Fired with the new selection on every change. */
    onChange: (ids: number[]) => void;
    /** Override the search placeholder. Defaults to a localized "Kërko proces...". */
    searchPlaceholder?: string;
    /** Override the empty-state message. */
    emptyMessage?: React.ReactNode;
    /** Extra class on the root for sizing/layout integration. */
    className?: string;
}

/**
 * Shared admin picker for selecting Responsibility ("process") IDs.
 *
 * - Search bar filters in real-time
 * - Selected items show their selection-order index badge (1, 2, 3...) so
 *   admins can read the order at a glance (preserving the badge convention
 *   from the legacy picker)
 * - Selection state is fully controlled — parent owns the `selectedIds` array
 *
 * Replaces the duplicated process-checkbox logic that used to live in
 * Materials.tsx, ArticleListAndCreateForm.tsx, and the ArticlesNew create/edit
 * pages. Future PRs migrate the other consumers to this component.
 */
const ProcessPicker: React.FC<ProcessPickerProps> = ({
    options,
    selectedIds,
    onChange,
    searchPlaceholder,
    emptyMessage,
    className,
}) => {
    const intl = useIntl();
    const [search, setSearch] = useState('');

    // O(1) membership lookup for each item render
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => (o.label || '').toLowerCase().includes(q));
    }, [options, search]);

    const toggle = (id: number) => {
        if (selectedSet.has(id)) {
            onChange(selectedIds.filter(x => x !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    // Bulk action targets the *visible* (filtered) set — when there's a search
    // active, "Select all" only adds the matches, not the whole catalog. This
    // is the standard pattern (Gmail, GitHub) and lets users do scoped bulk
    // ops like "search 'cnc' then select all CNC processes".
    const filteredIds = useMemo(() => filtered.map(o => o.id), [filtered]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedSet.has(id));

    const handleBulk = () => {
        if (allFilteredSelected) {
            const filterSet = new Set(filteredIds);
            onChange(selectedIds.filter(x => !filterSet.has(x)));
        } else {
            const merged = Array.from(new Set([...selectedIds, ...filteredIds]));
            onChange(merged);
        }
    };

    const resolvedPlaceholder = searchPlaceholder
        || intl.formatMessage({ id: 'picker.searchPlaceholder', defaultMessage: 'Kërko proces...' });

    return (
        <div className={`pp-root ${className || ''}`}>
            <div className="pp-toolbar">
                <div className="pp-search">
                    <svg className="pp-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        placeholder={resolvedPlaceholder}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="pp-meta">
                    <FormattedMessage
                        id="picker.selectedCount"
                        defaultMessage="{selected}/{total} zgjedhur"
                        values={{ selected: selectedIds.length, total: options.length }}
                    />
                </div>
            </div>

            {filteredIds.length > 0 && (
                <div className="pp-bulk">
                    <button
                        type="button"
                        className="pp-bulk-btn"
                        onClick={handleBulk}
                    >
                        {allFilteredSelected ? (
                            <FormattedMessage id="picker.clearVisible" defaultMessage="Hiq të gjitha" />
                        ) : (
                            <FormattedMessage id="picker.selectVisible" defaultMessage="Zgjidh të gjitha" />
                        )}
                    </button>
                </div>
            )}

            <div className="pp-list">
                {filteredIds.length === 0 ? (
                    <div className="pp-empty">
                        {emptyMessage || (
                            search.trim() ? (
                                <FormattedMessage id="picker.empty.search" defaultMessage="Asnjë proces nuk përputhet me kërkimin." />
                            ) : (
                                <FormattedMessage id="picker.empty.list" defaultMessage="Nuk ka procese të disponueshme." />
                            )
                        )}
                    </div>
                ) : (
                    filtered.map(option => {
                        const isSelected = selectedSet.has(option.id);
                        // The numeric badge shows the selection order — same convention
                        // as the legacy picker. Useful when the admin cares about the
                        // order in which processes were chosen (e.g. as a hint about
                        // workflow precedence, even though storage isn't ordered).
                        const selectionIndex = isSelected ? selectedIds.indexOf(option.id) + 1 : null;

                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={`pp-item ${isSelected ? 'pp-item--selected' : ''}`}
                                onClick={() => toggle(option.id)}
                                aria-pressed={isSelected}
                            >
                                <span className={`pp-check ${isSelected ? 'pp-check--on' : ''}`}>
                                    {isSelected && (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </span>
                                <span className="pp-label">{option.label}</span>
                                {selectionIndex !== null && (
                                    <span className="pp-index">{selectionIndex}</span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ProcessPicker;
